import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import API from "../Network/API";
import { useDispatch, useSelector } from "react-redux";
import {
  LOCK_ALARM_HISTORY_FETCH_ERROR,
  LOCK_ALARM_HISTORY_FETCH_SUCCESS,
  normalizeLockAlarmHistoryPayload,
} from "../Reducer/LockAlarmHistorySlice";

import { DOOR_ALARM_TYPE_FETCH_START, DOOR_ALARM_TYPE_FETCH_SUCCESS } from "../Reducer/DoorAlarmTypeSlice";
import { toast } from "react-hot-toast";
import ReactEcharts from "echarts-for-react";
import Agent from "../Network/Agent";
import CountdownTimer from "../Props/CountdownTimer";

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

const ALARM_ICONS = {
  1: "fa-sensor-on",
  2: "fa-lock-open",
  3: "fa-bell",
  4: "fa-clock",
  5: "fa-bullhorn",
  6: "fa-shield",
  7: "fa-door-open",
  8: "fa-door-closed",
  9: "fa-wave-square",
  10: "fa-lock-open",
  11: "fa-circle-check",
};

/** Per-type colors: icon circle + count circle (mockup) */
const ALARM_TYPE_THEME = {
  9: { bg: "#ede9fe", icon: "#6d28d9", countBg: "#fee2e2", count: "#dc2626" },
  10: { bg: "#ffedd5", icon: "#9a3412", countBg: "#ffedd5", count: "#ea580c" },
  5: { bg: "#fef9c3", icon: "#ca8a04", countBg: "#fef08a", count: "#ca8a04" },
  2: { bg: "#ede9fe", icon: "#7c3aed", countBg: "#f3e8ff", count: "#7c3aed" },
  6: { bg: "#dbeafe", icon: "#e11d48", countBg: "#dbeafe", count: "#e11d48" },
  3: { bg: "#ffe4e6", icon: "#e11d48", countBg: "#ffe4e6", count: "#e11d48" },
  7: { bg: "#ffedd5", icon: "#c2410c", countBg: "#ffedd5", count: "#ea580c" },
  8: { bg: "#dcfce7", icon: "#16a34a", countBg: "#dcfce7", count: "#16a34a" },
  11: { bg: "#dcfce7", icon: "#16a34a", countBg: "#dcfce7", count: "#16a34a" },
  1: { bg: "#dbeafe", icon: "#2563eb", countBg: "#dbeafe", count: "#2563eb" },
  4: { bg: "#f3e8ff", icon: "#9333ea", countBg: "#f3e8ff", count: "#9333ea" },
};

const DEFAULT_TYPE_THEME = {
  bg: "#f1f5f9",
  icon: "#64748b",
  countBg: "#f1f5f9",
  count: "#64748b",
};

function alarmIcon(orderList) {
  return ALARM_ICONS[orderList] ?? "fa-bell";
}

function alarmTypeTheme(orderList) {
  return ALARM_TYPE_THEME[orderList] ?? DEFAULT_TYPE_THEME;
}

function typeThemeStyle(orderList, part = "icon") {
  const t = alarmTypeTheme(orderList);
  if (part === "count") {
    return { backgroundColor: t.countBg, color: t.count };
  }
  return { backgroundColor: t.bg, color: t.icon };
}

function historyIconStyle(alarmType) {
  return typeThemeStyle(alarmType, "icon");
}

/** Active = type color; Cleared = green (mockup) */
function historyStatusStyle(alarmType, isActive) {
  if (!isActive) {
    return { backgroundColor: "#dcfce7", color: "#15803d" };
  }
  return typeThemeStyle(alarmType, "count");
}

function buildHistoryUrl({ page, perPage, query, site_code, start_date, end_date }) {
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  const typeIds = Array.isArray(query) ? query.filter(Boolean) : [];
  const sites = Array.isArray(site_code) ? site_code.filter(Boolean) : site_code ? [site_code] : [];
  if (typeIds.length) params.set("query", typeIds.join(","));
  if (sites.length) params.set("site_code", sites.join(","));
  if (start_date) params.set("start_date", start_date);
  if (end_date) params.set("end_date", end_date);
  return `/v1/oauth/lock_alarm_history?${params.toString()}`;
}

function buildGraphStatsUrl({ query, site_code, start_date, end_date, days = "7" }) {
  const params = new URLSearchParams({ scoped: "1", days: String(days) });
  const typeIds = Array.isArray(query) ? query.filter(Boolean) : [];
  const sites = Array.isArray(site_code) ? site_code.filter(Boolean) : site_code ? [site_code] : [];
  if (typeIds.length) params.set("query", typeIds.join(","));
  if (sites.length) params.set("site_code", sites.join(","));
  if (start_date) params.set("start_date", start_date);
  if (end_date) params.set("end_date", end_date);
  return `/v1/oauth/alarm_statistics?${params.toString()}`;
}

function formatAlarmId(row) {
  if (row?.id == null) return "—";
  return `ALM-${row.id}`;
}

function hasPartialDateRange(start, end) {
  return Boolean((start && !end) || (!start && end));
}

export default function Alarm() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const selector_lock = useSelector((state) => state.lockAlarmListReducer);

  const [alarmTypes, setAlarmTypes] = useState([]);
  const [siteFilter, setSiteFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tableSearch, setTableSearch] = useState("");

  const [perPage, setPerPage] = useState(10);
  const [alarms, setAlarms] = useState([]);
  const [meta, setMeta] = useState({
    total: 0,
    current_page: 1,
    per_page: 10,
    has_more_page: false,
  });
  const [loading, setLoading] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const [graphStats, setGraphStats] = useState(null);
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphRange, setGraphRange] = useState("7");

  const fetchIdRef = useRef(0);
  const graphAbortRef = useRef(null);
  const graphDebounceRef = useRef(null);

  const fetchAlarmHistory = useCallback(
    (page, overrides = {}) => {
      const fetchId = ++fetchIdRef.current;
      setLoading(true);

      const params = {
        page,
        perPage: overrides.perPage ?? perPage,
        query: overrides.query ?? alarmTypes,
        site_code: overrides.site_code ?? (siteFilter.trim() ? [siteFilter.trim()] : []),
        start_date: overrides.start_date ?? startDate,
        end_date: overrides.end_date ?? endDate,
      };

      return API.get(buildHistoryUrl(params))
        .then((res) => {
          if (fetchId !== fetchIdRef.current) return;
          const payload = normalizeLockAlarmHistoryPayload(res.data?.data ?? {});
          setAlarms(payload.alarms ?? []);
          const currentPage = Number(payload.current_page ?? page);
          setMeta({
            total: Number(payload.total_page ?? 0),
            current_page: currentPage,
            per_page: Number(payload.per_page ?? perPage),
            has_more_page: !!payload.has_more_page,
          });
          dispatch(LOCK_ALARM_HISTORY_FETCH_SUCCESS(payload));
        })
        .catch(() => {
          if (fetchId !== fetchIdRef.current) return;
          setAlarms([]);
          dispatch(LOCK_ALARM_HISTORY_FETCH_ERROR());
        })
        .finally(() => {
          if (fetchId === fetchIdRef.current) setLoading(false);
        });
    },
    [alarmTypes, siteFilter, startDate, endDate, perPage, dispatch]
  );

  const fetchGraphStats = useCallback(
    (overrides = {}, { immediate = false } = {}) => {
      const run = () => {
        graphAbortRef.current?.abort();
        const controller = new AbortController();
        graphAbortRef.current = controller;

        const params = {
          query: overrides.query ?? alarmTypes,
          site_code: overrides.site_code ?? (siteFilter.trim() ? [siteFilter.trim()] : []),
          start_date: overrides.start_date ?? startDate,
          end_date: overrides.end_date ?? endDate,
          days: overrides.days ?? graphRange,
        };

        setGraphLoading(true);
        return API.get(buildGraphStatsUrl(params), { signal: controller.signal })
          .then((res) => {
            if (controller.signal.aborted) return;
            setGraphStats(res.data?.data ?? null);
          })
          .catch((err) => {
            if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
            setGraphStats(null);
          })
          .finally(() => {
            if (!controller.signal.aborted) setGraphLoading(false);
          });
      };

      clearTimeout(graphDebounceRef.current);
      if (immediate) {
        run();
        return;
      }
      graphDebounceRef.current = setTimeout(run, 450);
    },
    [alarmTypes, siteFilter, startDate, endDate, graphRange]
  );

  useEffect(() => {
    Agent.ACS_ALARM.acs_alarm_nav(dispatch);

    const typeId = parseInt(searchParams.get("type"), 10);
    const initialTypes = !Number.isNaN(typeId) && typeId > 0 ? [typeId] : [];
    if (initialTypes.length) setAlarmTypes(initialTypes);

    fetchAlarmHistory(1, { query: initialTypes });
    fetchGraphStats({ query: initialTypes }, { immediate: true });

    return () => {
      clearTimeout(graphDebounceRef.current);
      graphAbortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    const typeId = parseInt(searchParams.get("type"), 10);
    if (!typeId || Number.isNaN(typeId)) return;
    setAlarmTypes((prev) => (prev.includes(typeId) ? prev : [...prev, typeId]));
    dispatch(DOOR_ALARM_TYPE_FETCH_SUCCESS(typeId));
  }, [searchParams, dispatch]);

  const lockAlarms = selector_lock?.data?.lock_alarms ?? [];

  const toggleAlarmType = (id) => {
    setAlarmTypes((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      fetchAlarmHistory(1, { query: next });
      fetchGraphStats({ query: next });
      return next;
    });
  };

  const applyFilters = () => {
    if (hasPartialDateRange(startDate, endDate)) {
      toast.error("Select both start and end date, or leave both empty");
      return;
    }
    fetchAlarmHistory(1);
    fetchGraphStats({}, { immediate: true });
  };

  const clearFilters = () => {
    setAlarmTypes([]);
    setSiteFilter("");
    setStartDate("");
    setEndDate("");
    setTableSearch("");
    dispatch(DOOR_ALARM_TYPE_FETCH_START());
    window.history.replaceState(null, "", "/web/alarms");
    const cleared = { query: [], site_code: [], start_date: "", end_date: "" };
    fetchAlarmHistory(1, cleared);
    fetchGraphStats(cleared, { immediate: true });
  };

  const saveDownload = async () => {
    if (hasPartialDateRange(startDate, endDate)) {
      toast.error("Select both start and end date, or leave both empty");
      return;
    }
    if (!startDate) {
      toast.error("Select start date to export");
      return;
    }
    setLoadingExport(true);
    try {
      const response = await Agent.ACS_ALARM.export_acs_alarm({
        query: alarmTypes.join(","),
        site_code: siteFilter.trim(),
        start_date: startDate,
        end_date: endDate || startDate,
      });
      const cd = response.headers?.["content-disposition"];
      let filename = "acs_alarm_report.xlsx";
      if (cd) {
        const m = /filename[^;=\n]*=(['"]?)([^'";\n]*)\1/.exec(cd);
        if (m?.[2]) filename = decodeURIComponent(m[2]);
      }
      const blob = new Blob([response.data], {
        type:
          response.headers?.["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      setLoadingExport(false);
    }
  };

  const filteredAlarms = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    if (!q) return alarms;
    return alarms.filter((row) => {
      const hay = [
        row.site_id,
        row.name,
        row.id,
        formatAlarmId(row),
        row.tenant_name,
        row.alarm_start,
        row.alarm_end,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [alarms, tableSearch]);

  const totalPages = Math.max(1, Math.ceil(meta.total / meta.per_page));
  const pageStart = meta.total === 0 ? 0 : (meta.current_page - 1) * meta.per_page + 1;
  const pageEnd = Math.min(meta.current_page * meta.per_page, meta.total);

  const graphOption = useMemo(() => {
    const trend = graphStats?.trend_7_days ?? [];
    if (!trend.length) return null;
    return {
      grid: { left: 48, right: 24, top: 24, bottom: 40 },
      tooltip: { trigger: "axis" },
      xAxis: {
        type: "category",
        data: trend.map((d) => d.date),
        axisLine: { lineStyle: { color: "#cbd5e1" } },
      },
      yAxis: {
        type: "value",
        axisLine: { show: false },
        splitLine: { lineStyle: { color: "#f1f5f9" } },
      },
      series: [
        {
          name: "Alarms",
          type: "line",
          smooth: true,
          symbol: "circle",
          symbolSize: 8,
          lineStyle: { width: 3, color: "#2563eb" },
          itemStyle: { color: "#2563eb" },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(37, 99, 235, 0.25)" },
                { offset: 1, color: "rgba(37, 99, 235, 0.02)" },
              ],
            },
          },
          data: trend.map((d) => d.count),
        },
      ],
    };
  }, [graphStats]);

  const pageNumbers = useMemo(() => {
    const pages = [];
    const cur = meta.current_page;
    const total = totalPages;
    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    if (cur > 3) pages.push("…");
    for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) pages.push(i);
    if (cur < total - 2) pages.push("…");
    pages.push(total);
    return pages;
  }, [meta.current_page, totalPages]);

  const goToPage = (p) => {
    if (p < 1 || p > totalPages || p === meta.current_page) return;
    fetchAlarmHistory(p);
  };

  return (
    <div className="acs-alarm-page px-3 pb-4">
      <header className="acs-alarm-page__header">
    
        <div className="acs-alarm-page__toolbar">
          <div className="acs-alarm-page__date-range">
            <input
              type="date"
              className="form-control form-control-sm"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              aria-label="Start date"
            />
            <span className="acs-alarm-page__date-sep">–</span>
            <input
              type="date"
              className="form-control form-control-sm"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              aria-label="End date"
            />
          </div>
          <input
            type="text"
            className="form-control form-control-sm acs-alarm-page__site-input"
            placeholder="Site ID"
            value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyFilters()}
            aria-label="Site ID filter"
          />
          <button type="button" className="btn btn-light btn-sm" onClick={applyFilters} title="Apply filters">
            <i className="fa-solid fa-search" />
          </button>
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={saveDownload} disabled={loadingExport}>
            <i className="fa-duotone fa-download me-1" />
            Export
            {loadingExport ? <span className="spinner-border spinner-border-sm ms-1" /> : null}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => {
              fetchAlarmHistory(meta.current_page);
              fetchGraphStats({}, { immediate: true });
            }}
          >
            <i className="fa-light fa-refresh me-1" />
            Sync
          </button>
        </div>
      </header>

      <div className="row g-3 acs-alarm-page__body">
        <div className="col-lg-3 col-md-4 acs-types-col">
          <aside className="acs-panel acs-panel--types acs-types-sticky">
            <h2 className="acs-panel__title acs-types-sticky__title">ALARM TYPES</h2>
            <div className="acs-types-scroll">
              <ul className="acs-type-list">
              {lockAlarms.map((item) => {
                const id = item.order_list;
                const active = alarmTypes.includes(id);
                const theme = alarmTypeTheme(id);
                return (
                  <li key={id}>
                    <button
                      type="button"
                      className={`acs-type-list__item ${active ? "acs-type-list__item--active" : ""}`}
                      style={active ? { "--type-accent": theme.count } : undefined}
                      onClick={() => toggleAlarmType(id)}
                    >
                      <span className="acs-type-list__icon" style={typeThemeStyle(id, "icon")}>
                        <i className={`fa-regular ${alarmIcon(id)}`} />
                      </span>
                      <span className="acs-type-list__label">{item.name}</span>
                      <span className="acs-type-list__count" style={typeThemeStyle(id, "count")}>
                        {item.value ?? 0}
                      </span>
                    </button>
                  </li>
                );
              })}
              </ul>
            </div>
            <button type="button" className="btn btn-outline-secondary btn-sm w-100 acs-types-clear" onClick={clearFilters}>
              <i className="fa-solid fa-filter-circle-xmark me-1" />
              Clear Filters
            </button>
          </aside>
        </div>

        <div className="col-lg-9 col-md-8">
          <section className="acs-panel acs-panel--history mb-3">
            <div className="acs-panel__head">
              <h2 className="acs-panel__title mb-0">ALARM HISTORY</h2>
              <div className="acs-panel__search">
                <i className="fa-light fa-search" />
                <input
                  type="search"
                  placeholder="Search alarm ID, type or details…"
                  value={tableSearch}
                  onChange={(e) => setTableSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="table-responsive acs-table-wrap">
              <table className="table table-hover acs-alarm-table mb-0">
                <thead>
                  <tr>
                    <th>Alarm Type</th>
                    <th>Alarm ID</th>
                    <th>Status</th>
                    <th>Start Time</th>
                    <th>End Time</th>
                    <th>Duration</th>
                    <th className="text-end">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-5 text-muted">
                        <span className="spinner-border spinner-border-sm me-2" />
                        Loading alarms…
                      </td>
                    </tr>
                  ) : filteredAlarms.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-5 text-muted">
                        No alarm records for this filter.
                      </td>
                    </tr>
                  ) : (
                    filteredAlarms.map((row) => {
                      const isActive = row.alarm_end == null;
                      const open = expandedId === row.id;
                      const typeId = row.alarm_type;
                      return (
                        <Fragment key={row.id ?? `${row.site_id}-${row.alarm_start}`}>
                          <tr>
                            <td>
                              <div className="acs-alarm-type-cell">
                                <span className="acs-alarm-type-cell__icon" style={historyIconStyle(typeId)}>
                                  <i className={`fa-regular ${alarmIcon(typeId)}`} />
                                </span>
                                <span>
                                  <span className="acs-alarm-type-cell__name">{row.name ?? "—"}</span>
                                  <span className="acs-alarm-type-cell__site">
                                    <Link className="fw-semibold" to={`/site/${row.site_id}`}>{row.site_id}</Link>
                                  </span>
                                </span>
                              </div>
                            </td>
                            <td className="fw-semibold">{formatAlarmId(row)}</td>
                            <td>
                              <span className="acs-status-pill" style={historyStatusStyle(typeId, isActive)}>
                                {isActive ? "Active" : "Cleared"}
                              </span>
                            </td>
                            <td className="text-nowrap">{row.alarm_start ?? "—"}</td>
                            <td className="text-nowrap text-muted">
                              {row.alarm_end ?? "—"}
                            </td>
                            <td className="text-nowrap">
                              {row.alarm_end != null ? (
                                row.total_elaspsed ?? "—"
                              ) : (
                                <CountdownTimer targetDate={row.start} />
                              )}
                            </td>
                            <td className="text-end">
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0"
                                onClick={() => setExpandedId(open ? null : row.id)}
                                aria-expanded={open}
                              >
                                <i className={`fa-solid fa-chevron-${open ? "up" : "down"}`} />
                              </button>
                            </td>
                          </tr>
                          {open ? (
                            <tr key={`${row.id}-detail`} className="acs-alarm-detail-row">
                              <td colSpan={7}>
                                <div className="acs-alarm-detail">
                                  {row.alarm_type === 7 ? (
                                    <div className="row g-2 small">
                                      <div className="col-md-6">
                                        <strong>Door Open By:</strong>
                                        <br />
                                        {row.command_type === 1 ? (
                                          <>
                                            <strong>{row.command_details?.name}</strong>
                                            <br />
                                            Org: {row.command_details?.organization}
                                            <br />
                                            Mobile: {row.command_details?.phone}
                                            <br />
                                            Job: {row.command_details?.details}
                                          </>
                                        ) : (
                                          <>
                                            <strong>{row.user?.name}</strong>
                                            <br />
                                            Org: {row.user?.vendor_name}
                                            <br />
                                            Mobile: {row.user?.phone}
                                            <br />
                                            Card: {row.user?.card_number}
                                            <br />
                                            Job: {row.user?.job_name}
                                          </>
                                        )}
                                      </div>
                                      {row.command_type === 1 ? (
                                        <div className="col-md-6">
                                          <strong>Lock Command By:</strong>
                                          <br />
                                          {row.user?.name}
                                          <br />
                                          Org: {row.user?.vendor_name}
                                          <br />
                                          Mobile: {row.user?.phone}
                                        </div>
                                      ) : null}
                                    </div>
                                  ) : (
                                    <span className="text-muted">No additional details.</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <footer className="acs-pagination">
              <span className="acs-pagination__info">
                Showing {pageStart} to {pageEnd} of {meta.total.toLocaleString()} records
              </span>
              <nav className="acs-pagination__nav" aria-label="Alarm history pages">
                <button type="button" className="acs-page-btn" disabled={meta.current_page <= 1} onClick={() => goToPage(meta.current_page - 1)}>
                  ‹
                </button>
                {pageNumbers.map((p, i) =>
                  p === "…" ? (
                    <span key={`ellipsis-${i}`} className="acs-page-btn acs-page-btn--dots">
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      className={`acs-page-btn ${p === meta.current_page ? "acs-page-btn--active" : ""}`}
                      onClick={() => goToPage(p)}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  type="button"
                  className="acs-page-btn"
                  disabled={meta.current_page >= totalPages}
                  onClick={() => goToPage(meta.current_page + 1)}
                >
                  ›
                </button>
              </nav>
              <select
                className="form-select form-select-sm acs-pagination__size"
                value={perPage}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  setPerPage(next);
                  fetchAlarmHistory(1, { perPage: next });
                }}
              >
                {PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>
            </footer>
          </section>

          <section className="acs-panel acs-panel--graph">
            <div className="acs-panel__head">
              <h2 className="acs-panel__title mb-0">ALARM OVERVIEW (GRAPH VIEW)</h2>
              <div className="d-flex align-items-center gap-2">
                <select
                  className="form-select form-select-sm"
                  value={graphRange}
                  onChange={(e) => {
                    const days = e.target.value;
                    setGraphRange(days);
                    fetchGraphStats({ days }, { immediate: true });
                  }}
                  style={{ width: 130 }}
                >
                  <option value="7">Last 7 Days</option>
                  <option value="14">Last 14 Days</option>
                  <option value="30">Last 30 Days</option>
                </select>
                <button type="button" className="btn btn-light btn-sm" onClick={fetchGraphStats} title="Refresh graph">
                  <i className="fa-light fa-refresh" />
                </button>
              </div>
            </div>
            <div className="acs-graph-body">
              {graphLoading ? (
                <div className="acs-graph-empty">
                  <span className="spinner-border text-primary" />
                </div>
              ) : graphOption ? (
                <ReactEcharts option={graphOption} style={{ height: 280 }} notMerge lazyUpdate />
              ) : (
                <div className="acs-graph-empty">
                  <div className="acs-graph-empty__art" aria-hidden>
                    <i className="fa-regular fa-chart-line" />
                  </div>
                  <p className="fw-semibold mb-1">No alarm data available</p>
                  <p className="text-muted small mb-0">
                    Select a different date range or try again later to view alarm trends.
                  </p>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      <style>{`
        .acs-alarm-page {
          --acs-border: #e2e8f0;
          --acs-bg: #f8fafc;
          --acs-primary: #2563eb;
          min-height: calc(100vh - 120px);
        }
        .acs-alarm-page__header {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: flex-end;
          gap: 16px;
          margin-bottom: 5px;
        }
        .acs-alarm-page__title {
          font-size: 1.75rem;
          font-weight: 700;
          margin: 0;
          color: #0f172a;
        }
        .acs-alarm-page__sub {
          margin: 4px 0 0;
          color: #64748b;
          font-size: 0.9rem;
        }
        .acs-alarm-page__toolbar {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
        }
        .acs-alarm-page__date-range {
          display: flex;
          align-items: center;
          gap: 6px;
          background: #fff;
          border: 1px solid var(--acs-border);
          border-radius: 8px;
          padding: 4px 8px;
        }
        .acs-alarm-page__date-sep { color: #94a3b8; }
        .acs-alarm-page__site-input {
          width: 140px;
          border: 1px solid var(--acs-border);
          border-radius: 8px;
          background: #fff;
        }
        .acs-alarm-page__site-input::placeholder { color: #94a3b8; }

        .acs-panel {
          background: #fff;
          border: 1px solid var(--acs-border);
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
        }
        .acs-types-col {
          align-self: flex-start;
        }
        .acs-panel--types {
          padding: 0;
          height: auto;
        }
        .acs-types-sticky {
          position: sticky;
          top: 64px;
          z-index: 20;
          max-height: calc(100vh - 72px);
          display: flex;
          flex-direction: column;
          padding: 10px 12px 12px;
        }
        .acs-types-sticky__title {
          flex-shrink: 0;
          margin-bottom: 8px;
        }
        .acs-types-scroll {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          overflow-x: hidden;
          padding-right: 2px;
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 transparent;
        }
        .acs-types-scroll::-webkit-scrollbar {
          width: 4px;
        }
        .acs-types-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        .acs-types-clear {
          flex-shrink: 0;
          margin-top: 8px;
          padding-top: 6px;
          padding-bottom: 6px;
          font-size: 0.8rem;
        }
        .acs-panel--history,
        .acs-panel--graph {
          padding: 0;
          overflow: hidden;
        }
        .acs-panel__title {
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          color: #000000;
          margin: 0 0 12px;
        }
        .acs-panel__head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 14px 16px;
          border-bottom: 1px solid var(--acs-border);
        }
        .acs-panel__search {
          position: relative;
          min-width: 220px;
        }
        .acs-panel__search i {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          font-size: 0.85rem;
        }
        .acs-panel__search input {
          width: 100%;
          border: 1px solid var(--acs-border);
          border-radius: 8px;
          padding: 6px 10px 6px 30px;
          font-size: 0.85rem;
        }

        .acs-type-list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .acs-type-list__item {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 10px;
          min-height: 0;
          border: 1px solid #eef2f6;
          background: #f8fafc;
          border-radius: 10px;
          text-align: left;
          transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
          cursor: pointer;
        }
        .acs-type-list__item:hover {
          background: #f1f5f9;
          border-color: #e2e8f0;
        }
        .acs-type-list__item--active {
          background: #fff;
          border-color: var(--type-accent, #2563eb);
          box-shadow: 0 0 0 1px var(--type-accent, #2563eb);
        }
        .acs-type-list__icon {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8rem;
        }
        .acs-type-list__label {
          flex: 1;
          font-size: 0.78rem;
          font-weight: 600;
          color: #1e293b;
          line-height: 1.15;
        }
        .acs-type-list__count {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: 700;
          line-height: 1;
        }
        @media (max-height: 800px) {
          .acs-type-list { gap: 4px; }
          .acs-type-list__item { padding: 5px 8px; }
          .acs-type-list__icon { width: 26px; height: 26px; font-size: 0.72rem; }
          .acs-type-list__count { width: 24px; height: 24px; font-size: 0.7rem; }
          .acs-type-list__label { font-size: 0.72rem; }
        }

        .acs-table-wrap { max-height: 420px; overflow: auto; }
        .acs-alarm-table thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #f8fafc;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #64748b;
          border-bottom: 1px solid var(--acs-border);
          white-space: nowrap;
        }
        .acs-alarm-table tbody td {
          vertical-align: middle;
          font-size: 0.85rem;
          border-bottom: 1px solid #f1f5f9;
        }
        .acs-alarm-type-cell {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .acs-alarm-type-cell__icon {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.95rem;
        }
        .acs-alarm-type-cell__name {
          display: block;
          font-weight: 600;
          color: #0f172a;
        }
        .acs-alarm-type-cell__site {
          display: block;
          font-size: 0.75rem;
          color: #64748b;
        }
        .acs-status-pill {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 999px;
          font-size: 0.75rem;
          font-weight: 600;
          white-space: nowrap;
        }
        .acs-alarm-detail-row td {
          background: #f8fafc;
          border-bottom: 1px solid var(--acs-border);
        }
        .acs-alarm-detail { padding: 8px 4px; }

        .acs-pagination {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 12px 16px;
          border-top: 1px solid var(--acs-border);
          background: #fafafa;
        }
        .acs-pagination__info {
          font-size: 0.8rem;
          color: #64748b;
        }
        .acs-pagination__nav {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .acs-page-btn {
          min-width: 32px;
          height: 32px;
          border: 1px solid var(--acs-border);
          background: #fff;
          border-radius: 6px;
          font-size: 0.85rem;
          color: #334155;
        }
        .acs-page-btn:hover:not(:disabled) { background: #f1f5f9; }
        .acs-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .acs-page-btn--active {
          background: var(--acs-primary);
          border-color: var(--acs-primary);
          color: #fff;
        }
        .acs-page-btn--dots {
          border: none;
          background: transparent;
          cursor: default;
        }
        .acs-pagination__size { width: auto; min-width: 100px; }

        .acs-graph-body { padding: 8px 12px 16px; min-height: 300px; }
        .acs-graph-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 280px;
          text-align: center;
          padding: 24px;
        }
        .acs-graph-empty__art {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: #eff6ff;
          color: #93c5fd;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          margin-bottom: 12px;
        }
      `}</style>
    </div>
  );
}
